import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Container, Heading, Screen, Stack, Text } from '@fluent/ui';
import { useAuth } from '../auth/authStore.js';
import { getCourse, PHRASES_COURSE_ID } from '../content/course.js';
import { AppBottomNav } from '../nav/AppBottomNav.js';
import { usePhrasesView, usePrecacheLessonAudio } from '../phrases/hooks.js';
import { PhrasesLoading, PhrasesView } from '../phrases/PhrasesView.js';
import '../phrases/Phrases.css';

/**
 * ფრაზები tab (2026-10-18, PHRASES.md) — the phrases course A1 → C2. Not
 * a road like Home: a sticker album, one page per level (Tornike's pick).
 * A tap opens /learn/:id — the same lesson player, which shows the paywall
 * for a Pro lesson and returns here when done.
 *
 * The phrases lessons are not in the app's start-up bundle: opening this
 * screen fetches them (content/lessons.ts) — the album's skeleton shows
 * meanwhile, and a retry if the fetch fails.
 */
export function Phrases() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { view, pending, content, retry } = usePhrasesView();
  usePrecacheLessonAudio(pending ? null : (view?.nextLessonId ?? null));

  if (!user) return null;

  return (
    <Screen>
      {content === 'loading' ? (
        <PhrasesLoading levelCount={getCourse(PHRASES_COURSE_ID)?.units.length ?? 0} />
      ) : content === 'error' ? (
        <PhrasesLoadError onRetry={retry} />
      ) : view && view.levels.length > 0 ? (
        <PhrasesView
          view={view}
          pending={pending}
          onOpen={(lesson) => navigate(`/learn/${lesson.id}`)}
        />
      ) : (
        <PhrasesEmpty />
      )}
      <AppBottomNav />
    </Screen>
  );
}

/** The course is not in this build yet (content is written level by level). */
export function PhrasesEmpty() {
  const { t } = useTranslation();
  return (
    <Container size="sm">
      <div className="fluent-phrases__empty" data-testid="phrases-empty">
        <Heading level={2} as="h1">
          {t('phrases.title')}
        </Heading>
        <img src="/characters/sticker-18.png" alt="" aria-hidden="true" draggable={false} />
        <Heading level={4} as="h2">
          {t('phrases.empty_title')}
        </Heading>
        <Text tone="muted">{t('phrases.empty_body')}</Text>
      </div>
    </Container>
  );
}

/** The phrases lessons could not be fetched (offline before they were
 *  cached). The button tries again (`retryPhrasesLessons`: in place, and a
 *  page reload only when online). */
function PhrasesLoadError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <Container size="sm">
      <div className="fluent-phrases__empty" data-testid="phrases-load-error">
        <Heading level={2} as="h1">
          {t('phrases.title')}
        </Heading>
        <Stack gap={3} align="center">
          <Heading level={4} as="h2">
            {t('phrases.load_error_title')}
          </Heading>
          <Text tone="muted">{t('phrases.load_error_body')}</Text>
          <Button variant="primary" onClick={onRetry}>
            {t('phrases.retry')}
          </Button>
        </Stack>
      </div>
    </Container>
  );
}
